import axios from 'axios'

import type { InsuranceType } from '@/components/quotes/quoteCardTypes'
import type { DeclineDetail } from './insuranceIndication'
import {
  type InsuranceCoverage,
  type InsuranceOneTimeFee,
  type InsuranceQuoteStatus,
  Vendor,
} from './insuranceTypes'

/** A declined or errored quote's reason for staff, with the next step the backend picked. */
export interface QuoteDeclineDetail extends DeclineDetail {
  nextStep: string
}

export interface InsuranceQuote {
  id: string
  idShort: string
  dateCreated: string
  dateUpdated: string
  /** Policy start date (YYYY-MM-DD) the carrier priced; null when unknown. */
  pricedStartDate: string | null
  /** Staff only: why the carrier declined or failed this quote; null otherwise. */
  declineDetail?: QuoteDeclineDetail | null
  vendor: Vendor
  vendorId: string
  status: InsuranceQuoteStatus
  vendorName: string
  broker: string | null
  /** Computed note shown to the customer — this offer's own, else the carrier's default. */
  carrierNote: string | null
  /** This quote's own note, raw — edit this, not carrierNote. */
  brokerNote: string
  /** A broker marked this offer as the one they're steering the customer toward. */
  brokerRecommended: boolean
  /** Full name of whoever recommended it; null when nobody has. */
  brokerRecommendedByName: string | null
  /** Assigned InsuranceCarrier record; null falls back to vendor branding. */
  carrierId: string | null
  /** One name for every entity this carrier writes on; null where the row names no carrier
   *  and its vendor writes none either, so there is no brand to merge its answer under. */
  carrierBrand: string | null
  /** The paper a brand quoted on, when it quotes on several at once, e.g. "APEX". */
  carrierProgram: string | null
  /** Whether the state guaranty fund backstops this carrier's paper. */
  insuranceType: InsuranceType | null
  name: string
  metadata: Record<string, any>
  oneTimeFees: InsuranceOneTimeFee[]
  premium: number
  coverages: InsuranceCoverage[]
  summary: string
  pros: string
  cons: string
  indication: string | null
  application: string
  iconUrl: string
  iconAlt: string
}

const REAL_VENDOR_NAMES = new Set(['Nirvana Insurance', 'Corgi', 'Cover Whale'])

export function isDemoVendor(vendorName: string): boolean {
  return !REAL_VENDOR_NAMES.has(vendorName)
}

export function requestToBindQuote(orgId: string, quote: InsuranceQuote) {
  return axios.post(`/api/v1/insure/organisations/${orgId}/quotes/${quote.id}/request-to-bind/`)
}

export async function deleteQuote(orgId: string, quoteId: string): Promise<void> {
  await axios.delete(`/api/v1/insure/organisations/${orgId}/quotes/${quoteId}/`)
}
